import { css } from 'lit';
import { hostReset, floatingPanel, placementOrigins } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  floatingPanel('.panel', { durationVar: '--uwc-dd-duration', durationDefault: '140ms' }),
  placementOrigins,
  css`
    :host { display: inline-block; }
    :host([fluid]) { width: 100%; }

    /* ── Root wrapper (trigger + float label) ───────────────────────────────── */
    .dd-root {
      position: relative;
      display:  inline-flex;
      width:    100%;
    }

    /* ── Trigger (shared base) ──────────────────────────────────────────────── */
    .trigger {
      display:       inline-flex;
      align-items:   center;
      gap:           6px;
      width:         100%;
      padding:       ${t.space2} ${t.space3};
      font-family:   var(--uwc-font-family, inherit);
      font-size:     var(--uwc-dd-trigger-font-size, ${t.fontSizeMd});
      background:    var(--uwc-dd-trigger-bg,     ${t.surface});
      border:        var(--uwc-dd-trigger-border, 1px solid ${t.borderControl});
      border-radius: var(--uwc-dd-trigger-radius, ${t.radiusMd});
      color:         var(--uwc-dd-trigger-color,  ${t.text});
      cursor:        pointer;
      text-align:    left;
      user-select:   none;
      min-width:     var(--uwc-dd-trigger-min-width, 140px);
      /* Match uwc-textinput height — derived from the font-size token so it scales
         with the size / font presets. Resolves to 33px at the default scale. */
      min-height:    ${t.controlHeightMd};
      line-height:   1;
      transition:    border-color ${t.durationBase}, box-shadow ${t.durationBase};
      box-sizing:    border-box;
    }

    /* Reduce outer padding in multi-select mode — chips provide their own spacing */
    :host([multiple]) .trigger {
      padding: ${t.space1} ${t.space2};
    }
    .trigger:hover:not([disabled]):not(:disabled) {
      border-color: color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 40%, ${t.borderControl});
    }
    .trigger:focus-visible {
      outline:      none;
      border-color: var(--uwc-dd-trigger-focus-border, var(--uwc-color-primary, ${t.primary}));
      box-shadow:   0 0 0 3px color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 20%, transparent);
    }
    .trigger:disabled,
    .trigger[aria-disabled="true"] {
      background: var(--uwc-dd-trigger-disabled-bg, ${t.surfaceRaised});
      color:      ${t.textDisabled};
      cursor:     not-allowed;
      pointer-events: none;
    }
    .trigger--open {
      border-color: var(--uwc-dd-trigger-focus-border, var(--uwc-color-primary, ${t.primary}));
      box-shadow:   0 0 0 3px color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 20%, transparent);
    }

    /* Editable trigger — div wrapper holding an <input> */
    .trigger--editable { padding: 0; cursor: text; }
    .editable-input {
      flex:        1;
      min-width:   0;
      padding:     ${t.space2} ${t.space3};
      border:      none;
      outline:     none;
      background:  transparent;
      font-family: var(--uwc-font-family, inherit);
      font-size:   inherit;
      color:       inherit;
      cursor:      text;
    }
    .trigger--editable .trigger-actions { padding-right: ${t.space2}; }

    /* ── Sizes ──────────────────────────────────────────────────────────────── */
    :host([size="small"]) .trigger {
      padding:   0.25rem 0.625rem;
      font-size: ${t.fontSizeXs};
      min-height: ${t.controlHeightSm};
    }
    :host([size="small"]) .editable-input { padding: 0.25rem 0.625rem; }

    :host([size="large"]) .trigger {
      padding:   ${t.space2} ${t.space4};
      font-size: ${t.fontSizeMd};
      min-height: ${t.controlHeightLg};
    }
    :host([size="large"]) .editable-input { padding: ${t.space2} ${t.space4}; }

    /* ── Filled variant ─────────────────────────────────────────────────────── */
    :host([variant="filled"]) .trigger {
      background:   var(--uwc-dd-filled-bg, ${t.surfaceRaised});
      border-color: transparent;
    }
    :host([variant="filled"]) .trigger:hover:not([disabled]):not(:disabled) {
      background: color-mix(in oklab, ${t.surfaceRaised} 80%, #000);
    }
    :host([variant="filled"]) .trigger--open {
      border-color: var(--uwc-color-primary, ${t.primary});
      background:   var(--uwc-dd-filled-bg, ${t.surfaceRaised});
    }

    /* ── Invalid state ──────────────────────────────────────────────────────── */
    :host([invalid]) .trigger {
      border-color: var(--uwc-color-danger, ${t.danger});
    }
    :host([invalid]) .trigger--open,
    :host([invalid]) .trigger:focus-visible {
      border-color: var(--uwc-color-danger, ${t.danger});
      box-shadow:   0 0 0 3px color-mix(in oklab, var(--uwc-color-danger, ${t.danger}) 20%, transparent);
    }

    /* ── Float label ────────────────────────────────────────────────────────── */

    /* Base — label starts centred inside the trigger (acts as placeholder) */
    .float-label {
      position:       absolute;
      left:           calc(${t.space3} + 1px);
      top:            50%;
      transform:      translateY(-50%);
      font-size:      ${t.fontSizeSm};
      color:          ${t.textMuted};
      pointer-events: none;
      line-height:    1;
      padding-inline: 0.125rem;
      white-space:    nowrap;
      transition:
        top       0.18s ease,
        transform 0.18s ease,
        font-size 0.18s ease,
        color     0.18s ease;
    }

    /* ── "over": animates from inside → above the trigger border ──────────── */
    :host([float-label="over"]) .dd-root {
      margin-top: 1.5rem;
      overflow:   visible;
    }
    /* Active: float label above the trigger with a small gap */
    :host([float-label="over"]) .float-label.float-label--active {
      top:       0;
      transform: translateY(calc(-100% - 0.25rem));
      font-size: ${t.fontSizeXs};
      color:     var(--uwc-color-primary, ${t.primary});
    }
    /* While label is resting inside, hide the trigger's own placeholder text */
    :host([float-label="over"]) .trigger--empty .is-placeholder,
    :host([float-label="over"]) .trigger--empty .editable-input::placeholder {
      color: transparent;
    }

    /* ── "in": floats to INSIDE TOP — never crosses the border ────────────── */
    /* Extra top padding creates room so the small label and selected text don't overlap */
    :host([float-label="in"]) .trigger {
      padding-top:    1.125rem;
      min-height:     2.625rem;
      align-items:    flex-end;
      padding-bottom: 0.375rem;
    }
    /* Active: small label pinned to the inside-top of the trigger */
    :host([float-label="in"]) .float-label.float-label--active {
      top:       0.3125rem;
      transform: none;
      font-size: ${t.fontSizeXs};
      color:     var(--uwc-color-primary, ${t.primary});
    }
    /* While label is resting inside, hide the trigger's own placeholder text */
    :host([float-label="in"]) .trigger--empty .is-placeholder,
    :host([float-label="in"]) .trigger--empty .editable-input::placeholder {
      color: transparent;
    }

    /* ── "on": label sits ON (straddles) the upper border ─────────────────── */
    :host([float-label="on"]) .dd-root {
      margin-top: 0.75rem;
      overflow:   visible;
    }
    :host([float-label="on"]) .float-label {
      background: var(--uwc-dd-trigger-bg, ${t.surface});
      padding:    0 4px;
    }
    :host([float-label="on"]) .float-label.float-label--active {
      top:       0;
      transform: translateY(-50%);
      font-size: ${t.fontSizeXs};
      color:     var(--uwc-color-primary, ${t.primary});
    }
    /* hide the native placeholder text while the on-label is inside */
    :host([float-label="on"]) .trigger--empty .is-placeholder,
    :host([float-label="on"]) .trigger--empty .editable-input::placeholder {
      color: transparent;
    }

    /* ── Trigger label / placeholder ────────────────────────────────────────── */
    .trigger-label {
      flex:          1;
      overflow:      hidden;
      text-overflow: ellipsis;
      white-space:   nowrap;
    }
    .trigger-label.is-placeholder { color: var(--uwc-dd-trigger-placeholder, ${t.textMuted}); }

    .trigger-actions { display: flex; align-items: center; gap: ${t.space1}; flex-shrink: 0; }

    /* ── Caret ──────────────────────────────────────────────────────────────── */
    .caret { color: ${t.textMuted}; transition: transform 160ms ease; flex-shrink: 0; }
    .caret--open { transform: rotate(180deg); }

    /* ── Clear button ───────────────────────────────────────────────────────── */
    .clear-btn {
      display:         flex;
      align-items:     center;
      justify-content: center;
      color:           ${t.textMuted};
      cursor:          pointer;
      border-radius:   ${t.radiusFull};
      transition:      background ${t.durationFast}, color ${t.durationFast};
    }
    .clear-btn:hover { background: ${t.hoverBg}; color: ${t.text}; }

    /* ── Loading spinner ────────────────────────────────────────────────────── */
    .loading-icon {
      display:       block;
      width:         14px;
      height:        14px;
      border:        2px solid ${t.borderControl};
      border-top-color: var(--uwc-color-primary, ${t.primary});
      border-radius: ${t.radiusFull};
      animation:     dd-spin 600ms linear infinite;
      flex-shrink:   0;
    }
    @keyframes dd-spin { to { transform: rotate(360deg); } }

    /* ── Chips (multi-select) ───────────────────────────────────────────────── */
    .chips-wrapper { display: flex; flex-wrap: wrap; gap: ${t.space1}; flex: 1; min-width: 0; }
    .chip {
      display:       inline-flex;
      align-items:   center;
      gap:           ${t.space1};
      padding:       3px 0 3px ${t.space2};
      background:    var(--uwc-dd-chip-bg,    ${t.borderSubtle});
      color:         var(--uwc-dd-chip-color, ${t.textSecondary});
      border-radius: ${t.radiusSm};
      font-size:     ${t.fontSizeMd};
      white-space:   nowrap;
      line-height:   1;
    }
    .chip--overflow { background: ${t.borderSubtle}; color: ${t.textSecondary}; padding: ${t.space1} ${t.space2}; }
    .chip-remove {
      display:     flex;
      align-items: center;
      background:  none;
      border:      none;
      cursor:      pointer;
      color:       ${t.textMuted};
      padding:     0 ${t.space1} 0 0;
      transition:  color ${t.durationFast};
    }
    .chip-remove:hover { color: ${t.text}; }

    /* ── Floating panel (visual) ────────────────────────────────────────────── */
    .panel {
      background:    var(--uwc-dd-panel-bg,     ${t.surface});
      border:        var(--uwc-dd-panel-border, 1px solid ${t.border});
      border-radius: var(--uwc-dd-panel-radius, ${t.radiusMd});
      box-shadow:    var(--uwc-dd-panel-shadow, ${t.shadowLg});
      z-index:       var(--uwc-dd-z,            ${t.zFloat});
      min-width:     160px; /* overridden at runtime by afterPosition → trigger width */
      overflow:      hidden;
    }

    /* ── Panel header / footer slots ────────────────────────────────────────── */
    .panel-header {
      padding:       ${t.space2} ${t.space3};
      border-bottom: 1px solid ${t.borderSubtle};
      flex-shrink:   0;
    }
    .panel-footer {
      padding:    ${t.space2} ${t.space3};
      border-top: 1px solid ${t.borderSubtle};
      flex-shrink:0;
      font-size:  ${t.fontSizeXs};
      color:      ${t.textMuted};
    }

    /* ── Filter input ───────────────────────────────────────────────────────── */
    .filter-wrapper {
      display:       flex;
      align-items:   center;
      gap:           ${t.space2};
      padding:       ${t.space2} ${t.space3};
      border-bottom: 1px solid ${t.borderSubtle};
    }
    .filter-icon  { color: ${t.textDisabled}; flex-shrink: 0; }
    .filter-input {
      flex:       1;
      border:     none;
      outline:    none;
      font-family:var(--uwc-font-family, inherit);
      font-size:  ${t.fontSizeMd};
      color:      ${t.text};
      background: transparent;
    }
    .filter-input::placeholder { color: ${t.textDisabled}; }

    /* ── Options wrapper ────────────────────────────────────────────────────── */
    .options-wrapper {
      overflow-y: auto;
      /* max-height set via inline style (scrollHeight prop) for non-virtual */
    }

    /* ── Standard options list ──────────────────────────────────────────────── */
    .options-list { list-style: none; margin: 0; padding: ${t.space1} 0; }

    /* ── Virtual options scroll container ───────────────────────────────────── */
    .options-scroll {
      overflow-anchor: none;
      scrollbar-width: thin;
      scrollbar-color: ${t.borderControl} transparent;
    }
    .options-scroll::-webkit-scrollbar        { width: 6px; }
    .options-scroll::-webkit-scrollbar-track  { background: transparent; }
    .options-scroll::-webkit-scrollbar-thumb  {
      background:    ${t.borderControl};
      border-radius: ${t.radiusFull};
    }
    /*
     * Prevent every child from being chosen as a scroll-anchor element.
     * Without this, hovering triggers a _highlightIdx re-render → DOM mutation
     * inside the scroll container → browser adjusts scrollTop to keep the
     * anchored item in place → scroll event fires → range changes → re-render
     * → runaway scroll to end-of-list.
     */
    .options-scroll * { overflow-anchor: none; }

    /* ── Group header ───────────────────────────────────────────────────────── */
    .group-header {
      display:        flex;
      align-items:    center;
      gap:            7px;
      padding:        ${t.space2} 13px 4px;
      font-size:      ${t.fontSizeXs};
      font-weight:    ${t.fontWeightBold};
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color:          var(--uwc-dd-group-color, ${t.textMuted});
      cursor:         default;
    }

    /* ── Option ─────────────────────────────────────────────────────────────── */
    .option {
      display:     flex;
      align-items: center;
      gap:         9px;
      padding: ${t.space2} 13px;
      font-size:   var(--uwc-dd-option-font-size, ${t.fontSizeMd});
      font-family: var(--uwc-font-family, inherit);
      color:       var(--uwc-dd-option-color, ${t.text});
      font-weight: var(--uwc-dd-option-font-weight, ${t.fontWeightNormal});
      cursor:      pointer;
      list-style:  none;
      overflow:    hidden;
      transition:  background ${t.durationFast};
    }
    .option:hover,
    .option--highlighted        { background: var(--uwc-dd-option-hover-bg, ${t.hoverBg}); }
    .option--selected {
      background:  var(--uwc-dd-option-selected-bg,    ${t.selectedBg});
      color:       var(--uwc-dd-option-selected-color, var(--uwc-color-primary, ${t.primary}));
      font-weight: var(--uwc-dd-option-selected-font-weight, ${t.fontWeightMedium});
    }
    .option--selected.option--highlighted {
      background: color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 15%, transparent);
    }
    .option--disabled           { color: ${t.textDisabled}; cursor: not-allowed; }
    .option--disabled:hover     { background: transparent; }

    .opt-icon  { width: 18px; text-align: center; flex-shrink: 0; }
    .opt-label { flex: 1; line-height: 1; }
    .opt-badge {
      font-size:     ${t.fontSizeXs};
      padding:       1px 6px;
      border-radius: ${t.radiusFull};
      background:    ${t.borderSubtle};
      color:         ${t.textSecondary};
    }
    .opt-check {
      display:         flex;
      align-items:     center;
      justify-content: center;
      color:           var(--uwc-color-primary, ${t.primary});
      margin-left:     auto;
      flex-shrink:     0;
    }

    /* ── Empty state ────────────────────────────────────────────────────────── */
    .empty {
      padding:    20px ${t.space4};
      text-align: center;
      font-size:  ${t.fontSizeXs};
      color:      ${t.textDisabled};
    }
  `,
];
